import React, { useMemo, FC, KeyboardEvent } from "react";
import cn from "classnames";
import TextareaAutosize from "react-textarea-autosize";
import { useTranslation } from "react-i18next";
import { useFormikContext, useField } from "formik";
import MaskedInput from "react-text-mask";
import PhoneInput from "react-phone-number-input/input";
import { IFormField } from "src/@types/form";
import { FormField } from ".";
import { TEXT_INPUT_STYLE_VARIANTS } from "./constants";
import { FormFieldVariants } from "./types";

export const TextField: FC<IFormField> = ({
  className,
  fieldClassName,
  labelClassName,
  id,
  label,
  variant = FormFieldVariants.PRIMARY,
  placeholder,
  isTextArea,
  errorClassName,
  ...props
}) => {
  const { t } = useTranslation();

  const name = props.name as string;

  const fieldId = id || name;

  const { handleSubmit } = useFormikContext();

  const [{ value, onChange, ...field }, { error, touched }, { setValue }] =
    useField(fieldId);

  const isShownError = Boolean((touched || value) && error);

  const onKeyDownEnter = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !isTextArea) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const placeholderText =
    typeof placeholder === "string"
      ? placeholder
      : placeholder && t(placeholder.i18nKey, placeholder.i18nParams);

  const combinedClassNames = cn(
    "w-full outline-none",
    TEXT_INPUT_STYLE_VARIANTS[variant],
    className,
    { "resize-y": isTextArea }
  );

  // const Tag = isTextArea ? "textarea" : "input";
  // const InputComponent = props?.mask ? MaskedInput : Tag;

  const isTelField = props?.type === "tel";

  const InputComponent = useMemo(() => {
    if (isTelField) {
      return PhoneInput;
    }
    if (props?.mask) {
      return MaskedInput;
    } else if (isTextArea) {
      return TextareaAutosize;
    } else {
      return "input";
    }
  }, [props?.mask, isTextArea, isTelField]);

  return (
    <FormField
      className={fieldClassName}
      labelClassName={labelClassName}
      variant={variant}
      label={label}
      labelFor={fieldId}
      isShownError={isShownError}
      error={error}
      errorClassName={errorClassName}
    >
      <InputComponent
        id={fieldId}
        className={combinedClassNames}
        value={value}
        placeholder={placeholderText}
        maxLength={isTelField ? 20 : null}
        onChange={isTelField ? setValue : onChange}
        {...props}
        {...field}
        // Placed here to override the Formik default onKeyDown function
        onKeyDown={onKeyDownEnter}
        // Placed here to prevent the number input from changing when scrolling
        onWheel={(e) => props.type === "number" && e.target.blur()}
      />
    </FormField>
  );
};
